import React, { useState, cloneElement, useEffect } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useDataProvider,
  EditContextProvider,
  useEditController,
  SelectInput,
  EditProps,
  Title,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography } from "@material-ui/core";
import TitleBreadcrumb from "components/TitleBreadcrumb";
import EditableDataGrid from "./components/EditableAreaDatagrid";
import { AcademicYearResources, GetParam } from "utils/tools";
import { labelDataGridStyles, labelStyle } from "utils/style";
import moment from "moment";
import CustomEditToolbar from "components/CustomEditToolbar";
import CustomEndDateInput from "components/CustomEndDateInput";
import { useField } from "react-final-form";
import CustomDatePickerInput from "components/CustomDatePickerInput";
import _ from "lodash";
import TbfCodeInput from "./components/TbfCodeInput";

const EditForm = (props: any) => {
  const { aside } = props;
  const controllerProps = useEditController(props);
  const {
    basePath, // deduced from the location, useful for action buttons
    loaded, // boolean that is false until the record is available
    record, // record fetched via dataProvider.getOne() based on the id from the location
    redirect, // the default redirection route. Defaults to 'list'
    resource, // the resource name, deduced from the location. e.g. 'posts'
    save, // the update callback, to be passed to the underlying form as submit handler
    saving, // boolean that becomes true when the dataProvider is called to update the record
    version, // integer used by the refresh feature
    defaultTitle,
  } = controllerProps;

  const formatRecord = (res: any) => {
    if (res) {
      if (res.startedAt) {
        res.startedAt = moment(res.startedAt).format("YYYY-MM-DD");
      }

      if (res.endedAt) {
        res.endedAt = moment(res.endedAt).format("YYYY-MM-DD");
      }

      if (res.invoiceIssuedAt) {
        res.invoiceIssuedAt = moment(res.invoiceIssuedAt).format("YYYY-MM-DD");
      }
      return res;
    }
  };

  return (
    <EditContextProvider value={controllerProps}>
      <div>
        <Typography variant="h4" style={labelStyle}>
          {record?.name}
        </Typography>

        <Title title={defaultTitle} />

        {loaded &&
          cloneElement(props.children, {
            className: "MuiPaper-root MuiPaper-elevation1",
            basePath,
            record: formatRecord(record),
            redirect,
            resource,
            save,
            saving,
            version,
          })}
        {loaded &&
          aside &&
          cloneElement(aside, {
            basePath,
            record,
            resource,
            version,
            save,
            saving,
          })}
      </div>
    </EditContextProvider>
  );
};

const RateChartEdit = (props: EditProps) => {
  const [areas, setAreas] = useState([]);
  const [idAcademicYear, setIdAcademicYear] = useState();
  const [status, setStatus] = useState<string>();
  const [disableForm, setDisableForm] = useState<boolean>(false);
  const [minDate, setMinDate] = useState<string>();
  const param = GetParam();
  const provider = useDataProvider();

  const breadcrumb = [
    { title: "Schools", link: `/schools` },
    { title: "Pricing", link: `/schools/${param.get("schoolId")}/show/5` },
    { title: "Edit", link: "" },
  ];

  //fetch area data for current school on first load
  useEffect(() => {
    provider
      .getList("areas", {
        filter: { SchoolId: Number(param.get("schoolId")) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "MaxDistanceInMeters",
          order: "ASC",
        },
      })
      .then((result) => {
        setAreas(result.data);
      });

    provider.getOne("rateCharts", { id: props.id }).then((result) => {
      setIdAcademicYear(result.data.academicYearId);
      setStatus(result.data.status);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== "active") {
      setDisableForm(true);
    } else {
      setDisableForm(false);
    }
  }, [status]);

  const transform = (data: any) => {
    data.rateChartItems = [
      ...data.oneTimeAWeekPricing,
      ...data.twoTimeAWeekPricing,
      ...data.threeTimeAWeekPricing,
      ...data.fourTimeAWeekPricing,
      ...data.fiveTimeAWeekPricing,
    ];

    delete data.oneTimeAWeekPricing;
    delete data.twoTimeAWeekPricing;
    delete data.threeTimeAWeekPricing;
    delete data.fourTimeAWeekPricing;
    delete data.fiveTimeAWeekPricing;

    delete data.updatedAt;

    return data;
  };

  const academicYearResources = AcademicYearResources(idAcademicYear);

  const CustomReferenceInput = () => {
    const startedAt = useField("startedAt");
    const endedAt = useField("endedAt");
    const invoiceIssuedAt = useField("invoiceIssuedAt");

    return (
      <ReferenceInput
        label="Select Academic Year"
        source="academicYearId"
        reference="academicYears"
        validate={[required()]}
        filter={{ SchoolId: Number(param.get("schoolId")) }}
        onChange={({ target }) => {
          setIdAcademicYear(target.value);
          startedAt.input.onChange("");
          endedAt.input.onChange("");
          invoiceIssuedAt.input.onChange("");
        }}
        disabled={disableForm}
      >
        <SelectInput optionText="name" optionId="id" />
      </ReferenceInput>
    );
  };

  useEffect(() => {
    if (idAcademicYear !== undefined) {
      provider
        .getList("rateCharts", {
          filter: { AcademicYearId: Number(idAcademicYear) },
          pagination: {
            page: 1,
            perPage: 9999,
          },
          sort: {
            field: "EndedAt",
            order: "Desc",
          },
        })
        .then((result) => {
          if (result.total > 0) {
            const rateChartBeforeDate = getTheRateChartBefore(
              result.data as IRateChart[]
            );

            setMinDate(
              moment.utc(rateChartBeforeDate).add(1, "d").format("yyyy-MM-DD")
            );
          } else {
            setMinDate(
              moment.utc(academicYearResources?.startDate).format("yyyy-MM-DD")
            );
          }
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idAcademicYear]);

  const getTheRateChartBefore = (rateCharts: IRateChart[]) => {
    const existingIndex = _.findIndex(rateCharts, ["id", Number(props.id)]);

    if (rateCharts.length - 1 === existingIndex) {
      return rateCharts[existingIndex].startedAt;
    }

    return rateCharts[existingIndex + 1].endedAt;
  };

  return (
    <>
      <TitleBreadcrumb paths={breadcrumb} />
      <EditForm undoable={false} transform={transform} {...props}>
        <SimpleForm
          toolbar={
            <CustomEditToolbar
              redirectLink={`/schools/${param.get("schoolId")}/show/5`}
              isDeleteWithConfirm
            />
          }
          name="manageRateChart"
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School & Academic Year</Typography>
            </Grid>

            <Grid item xs={3}>
              <ReferenceInput
                label="Select School"
                source="schoolId"
                reference="schools"
                validate={[required()]}
                disabled
              >
                <AutocompleteInput optionText="name" optionId="id" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <CustomReferenceInput />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <TbfCodeInput />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Pricing Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput
                source="name"
                label="Pricing Name"
                validate={required()}
                disabled={disableForm}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <CustomEndDateInput
                source="startedAt"
                label="Pricing Start Date"
                parentStartDate={minDate}
                parentEndDate={moment
                  .utc(academicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
                isDisabled={disableForm}
              />
            </Grid>

            <Grid item xs={3}>
              <CustomEndDateInput
                source="endedAt"
                label="Pricing End Date"
                parentStartDate={moment
                  .utc(academicYearResources?.startDate)
                  .format("yyyy-MM-DD")}
                parentEndDate={moment
                  .utc(academicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
                comparison="startedAt"
                isDisabled={disableForm}
              />
            </Grid>
            <Grid item xs={6}></Grid>

            <Grid item xs={3}>
              <CustomDatePickerInput
                name="invoiceIssuedAt"
                label="Invoice Issue Date"
                min={moment().format("Y-MM-DD")}
                disableValue={disableForm}
              />
            </Grid>

            {/* <Grid item xs={4}>
              <SelectInput
                source="isIncludeTax"
                label="Tax Included?"
                choices={[
                  { id: true, name: "Yes" },
                  { id: false, name: "No" },
                ]}
              />
            </Grid> */}

            <Grid item xs={12}>
              <Typography variant="h6">
                Area Pricing (Inclusive of GST)
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  1 Time a Week
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="oneTimeAWeekPricing"
                  frequency={1}
                  isDisabled={false}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  2 Time a Week
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="twoTimeAWeekPricing"
                  frequency={2}
                  isDisabled={false}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  3 Time a Week
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="threeTimeAWeekPricing"
                  frequency={3}
                  isDisabled={false}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  4 Time a Week
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="fourTimeAWeekPricing"
                  frequency={4}
                  isDisabled={false}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  5 Time a Week
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="fiveTimeAWeekPricing"
                  frequency={5}
                  isDisabled={false}
                />
              </Grid>
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </EditForm>
    </>
  );
};

export default RateChartEdit;
